import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';

import { getUserHostingQueryKey } from '@newfold/huapi-js';

import { HOSTING_LIST_PAGE_SS_KEY } from '~/components/TenantProvider/utils/constants';
import { HOSTING_FETCH_LIMIT } from '~/scenes/Sites/utils/constants';
import { ADDON_AGE_THRESHOLD_MS } from '~/utils/hosting/constants';
import { NullableString } from '~/utils/hosting/types';

const parseUtcTimestamp = (date?: string | null): number | null => {
  if (!date) {
    return null;
  }

  const utcDate = `${date.replace(' ', 'T')}Z`;

  const timestamp = new Date(utcDate).getTime();

  if (Number.isNaN(timestamp)) {
    return null;
  }

  return timestamp;
};

const getAddonAgeInMs = (addonTimestamp: number): number =>
  Date.now() - addonTimestamp;

const getIsAddonPurchaseDelayElapsed = (addonTimestamp: number | null) => {
  if (addonTimestamp === null) {
    return true;
  }

  const addonAgeInMs = getAddonAgeInMs(addonTimestamp);

  return addonAgeInMs >= ADDON_AGE_THRESHOLD_MS;
};

const useAddonPurchaseDelayElapsed = (
  addonDateAdded?: NullableString,
  hostingStatus?: NullableString,
  enabled: boolean = false,
) => {
  const queryClient = useQueryClient();
  const nextPage = Number(
    sessionStorage.getItem(HOSTING_LIST_PAGE_SS_KEY) ?? 1,
  );

  const addonTimestamp = useMemo(
    () => parseUtcTimestamp(addonDateAdded),
    [addonDateAdded],
  );

  const [hasAddonPurchaseDelayElapsed, setHasAddonPurchaseDelayElapsed] =
    useState(() =>
      enabled ? getIsAddonPurchaseDelayElapsed(addonTimestamp) : true,
    );

  useEffect(() => {
    if (!enabled) {
      setHasAddonPurchaseDelayElapsed(true);
      return;
    }

    if (addonTimestamp === null) {
      setHasAddonPurchaseDelayElapsed(true);
      return;
    }

    const remainingMs =
      ADDON_AGE_THRESHOLD_MS - getAddonAgeInMs(addonTimestamp);

    if (remainingMs <= 0) {
      setHasAddonPurchaseDelayElapsed(true);
      return;
    }

    setHasAddonPurchaseDelayElapsed(false);
    const timer = setTimeout(() => {
      if (hostingStatus !== 'active') {
        queryClient.invalidateQueries({
          queryKey: getUserHostingQueryKey({
            page: 1,
            limit: nextPage * HOSTING_FETCH_LIMIT,
          }),
        });
        queryClient.invalidateQueries({
          queryKey: getUserHostingQueryKey({
            page: nextPage,
            limit: 3,
          }),
        });
      }
      setHasAddonPurchaseDelayElapsed(true);
    }, remainingMs);

    return () => clearTimeout(timer);
  }, [addonTimestamp, enabled, hostingStatus, nextPage, queryClient]);

  return enabled ? hasAddonPurchaseDelayElapsed : true;
};

export default useAddonPurchaseDelayElapsed;
